import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import GrowthShell from '../../components/growth/GrowthShell';
import RangeBar from '../../components/growth/GrowthRange';
import { eventAr } from '../../lib/growthLabels';
import { fmtAt, rangeLabel, rangeWindow, readRange, spanLabel, writeRange } from '../../lib/growthRange';
import { playerUrl, recordingUrl } from '../../lib/claritySessions';
import {
  STAGES, applyFilters, describe, deviceAr, fetchSessionTimeline, fetchSessions, fetchSessionsByIds,
  fmtDuration, groupTimeline, hiddenRunText, stageAr, stoppedAt,
} from '../../lib/growthSessions';

// Growth OS «سجل الجلسات» — owner, 2026-11-01. The list follows the range and
// four filters (all in the URL); ?s=<session> opens that session's timeline,
// ?ids=a,b,c shows exactly the sessions a «شاهد الجلسات» list pointed at.

const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const PAGE = 100;
const timeOf = new Intl.DateTimeFormat('ar-SA-u-ca-gregory-nu-latn', { timeZone: 'Asia/Riyadh', hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
const exactTime = (ms) => timeOf.format(new Date(ms));

function Filters({ sessions, f, set }) {
  const sources = useMemo(() => {
    const m = {};
    sessions.forEach((s) => { m[s.source.key] = s.source.ar.replace(' · إعلان', '') + (s.source.ar.includes('إعلان') ? ' (إعلان)' : ''); });
    return Object.entries(m).sort((a, b) => a[1].localeCompare(b[1], 'ar'));
  }, [sessions]);
  const devices = useMemo(() => [...new Set(sessions.map((s) => s.device).filter(Boolean))], [sessions]);
  const sel = (key, label, opts) => (
    <label className="gos-filter">
      <span>{label}</span>
      <select value={f[key] || ''} onChange={(e) => set(key, e.target.value)} data-filter={key}>
        <option value="">الكل</option>
        {opts.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
      </select>
    </label>
  );
  return (
    <div className="gos-filters" role="group" aria-label="تصفية">
      {sel('src', 'المصدر', sources)}
      {sel('dev', 'الجهاز', devices.map((d) => [d, deviceAr(d)]))}
      {sel('stage', 'وصل إلى', STAGES.map((s) => [s.key, s.ar]))}
      {sel('auth', 'الحساب', [['in', 'مسجّل الدخول'], ['out', 'زائر']])}
    </div>
  );
}

function SessionList({ params, setParams, win, ids }) {
  const [data, setData] = useState(undefined);
  const [shown, setShown] = useState(PAGE);
  useEffect(() => {
    let alive = true; setData(undefined); setShown(PAGE);
    (ids ? fetchSessionsByIds(ids) : fetchSessions(win)).then((d) => { if (alive) setData(d); })
      .catch((e) => { console.warn('[GrowthSessions]', e?.message || e); if (alive) setData(null); });
    return () => { alive = false; };
  }, [win.start, win.end, ids && ids.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  const f = { src: params.get('src'), dev: params.get('dev'), stage: params.get('stage'), auth: params.get('auth') };
  const setF = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p); };
  const open = (id) => { const p = new URLSearchParams(params); p.set('s', id); setParams(p); window.scrollTo(0, 0); };
  if (data === undefined) return <div className="gos-card"><div className="gos-empty-state">…</div></div>;
  if (data === null) return <div className="gos-card"><div className="gos-empty-state"><strong>تعذّر التحميل</strong>أعد تحميل الصفحة.</div></div>;
  const list = applyFilters(data.sessions, f);
  const signed = list.filter((s) => s.signedIn).length;
  return (
    <section className="gos-card" data-section="sessions">
      <div className="gos-card-head">
        <h3>{ids ? 'الجلسات المختارة' : 'كل الجلسات'}</h3>
        <span className="gos-pcount" data-count={list.length}>{fmt(list.length)} جلسة · {fmt(signed)} مسجّل</span>
      </div>
      {ids && <p className="gos-card-sub">الجلسات التي فتحتها من قائمة «شاهد الجلسات». <Link className="gos-link" to="/admin2/growth/sessions">كل الجلسات في الفترة</Link></p>}
      <Filters sessions={data.sessions} f={f} set={setF} />
      {list.length === 0 ? <div className="gos-empty-state"><strong>لا جلسات</strong>{data.sessions.length ? 'لا شيء يطابق التصفية.' : 'لا توجد جلسات في هذه الفترة.'}</div> : (
        <div className="gos-table-scroll">
          <table className="gos-table gos-sessions" data-sessions="1">
            <thead><tr>
              <th>الوقت</th><th>المدة</th><th>المصدر</th><th className="gos-hide-sm">الجهاز</th><th className="gos-hide-sm">أول صفحة</th>
              <th className="gos-hide-sm">الصفحات</th><th>وصل إلى</th><th className="gos-hide-sm">الحساب</th>
            </tr></thead>
            <tbody>{list.slice(0, shown).map((s) => (
              <tr key={s.session_id} data-session-row={s.session_id} onClick={() => open(s.session_id)} tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') open(s.session_id); }} className="gos-click">
                <td className="gos-when">{fmtAt(s.at)}</td>
                <td className="gos-when">{fmtDuration(s.seconds)}</td>
                <td>{s.source.ar}</td>
                <td className="gos-hide-sm">{deviceAr(s.device)}{s.os ? ` · ${s.os}` : ''}</td>
                <td className="gos-hide-sm" dir="ltr" style={{ textAlign: 'right', wordBreak: 'break-all' }}>{s.landing || '—'}</td>
                <td className="num gos-hide-sm">{fmt(s.pages)}</td>
                <td><span className={`gos-stage s-${s.stage}`}>{stageAr(s.stage)}</span></td>
                <td className="gos-hide-sm">{s.signedIn ? 'مسجّل' : 'زائر'}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {list.length > shown && <button type="button" className="gos-btn ghost" style={{ marginTop: 12 }} onClick={() => setShown(shown + PAGE)}>عرض المزيد ({fmt(list.length - shown)})</button>}
      {data.capped && <p className="gos-note">الفترة فيها أحداث أكثر مما يُحمَّل مرة واحدة؛ اختر فترة أقصر لترى كل الجلسات.</p>}
      <p className="gos-note">جلسات حسابات الفريق مستبعدة. المدينة لا تُجمع هنا؛ تظهر داخل تسجيل Clarity.</p>
    </section>
  );
}

function Timeline({ id, onBack }) {
  const [t, setT] = useState(undefined);
  useEffect(() => {
    let alive = true; setT(undefined);
    fetchSessionTimeline(id).then((r) => { if (alive) setT(r); }).catch((e) => { console.warn('[GrowthSessions] timeline:', e?.message || e); if (alive) setT(null); });
    return () => { alive = false; };
  }, [id]);
  const recordings = useMemo(() => (t?.list || []).filter((e) => e.event_name === 'clarity_session_linked')
    .map((e) => ({ at: Date.parse(e.occurred_at), url: playerUrl(e.props?.clarity_user, e.props?.clarity_session), screen: e.screen }))
    .filter((r) => r.url), [t]);
  const s = t?.summary;
  const stop = t?.list ? stoppedAt(t.list) : null;
  return (
    <section className="gos-card gos-timeline" data-section="timeline" data-session={id}>
      <div className="gos-card-head">
        <h3>جلسة {s ? fmtAt(s.at) : ''}</h3>
        <button type="button" className="gos-btn ghost" onClick={onBack} data-back="1" style={{ flex: '0 0 auto' }}>رجوع للقائمة</button>
      </div>
      {t === undefined ? <div className="gos-empty-state">…</div> : t === null ? <div className="gos-empty-state">تعذّر التحميل.</div>
        : t.team ? <div className="gos-empty-state"><strong>جلسة من حساب الفريق</strong>لا تُعرض هنا.</div>
        : !t.list.length ? <div className="gos-empty-state"><strong>لا توجد أحداث لهذه الجلسة</strong></div> : (
          <>
            <div className="gos-rec" data-recordings={recordings.length}>
              {recordings.length > 0 ? (
                <>
                  <a className="gos-btn" href={recordings[0].url} target="_blank" rel="noopener noreferrer" data-play="1">▶ تشغيل التسجيل في Clarity</a>
                  {recordings.length > 1 && <span className="gos-faint">{fmt(recordings.length)} تسجيلات — Clarity يبدأ تسجيلاً جديداً عند كل صفحة؛ كل واحد مربوط بموضعه أدناه.</span>}
                </>
              ) : recordingUrl(id) ? (
                <a className="gos-btn ghost" href={recordingUrl(id, s?.at)} target="_blank" rel="noopener noreferrer" data-play="search">بحث عن الجلسة في Clarity</a>
              ) : null}
            </div>
            {s && (
              <div className="gos-sfacts">
                <span><b>المدة</b> {fmtDuration(s.seconds)}</span>
                <span><b>المصدر</b> {s.source.ar}</span>
                <span><b>الجهاز</b> {deviceAr(s.device)}{s.os ? ` · ${s.os}` : ''}</span>
                <span><b>أول صفحة</b> <bdi dir="ltr">{s.landing || '—'}</bdi></span>
                <span><b>الصفحات</b> {fmt(s.pages)}</span>
                <span><b>الحساب</b> {s.signedIn ? 'مسجّل الدخول' : 'زائر'}</span>
                <span><b>وصل إلى</b> {stageAr(s.stage)}</span>
                {stop && <span data-stopped="1"><b>توقف عند</b> {stop.label}{stop.screen ? <> · <bdi dir="ltr">{stop.screen}</bdi></> : null}</span>}
              </div>
            )}
            <ol className="gos-events">
              {groupTimeline(t.list).map((item) => {
                const { e } = item;
                const rec = e.event_name === 'clarity_session_linked' ? playerUrl(e.props?.clarity_user, e.props?.clarity_session) : null;
                const run = hiddenRunText(item);
                const d = run ? e.screen : describe(e, t.products);
                return (
                  <li key={e.event_id} data-event={e.event_name} data-count={item.count}>
                    <time>{exactTime(Date.parse(e.occurred_at))}{run && <> – {exactTime(Date.parse(item.last.occurred_at))}</>}</time>
                    <div>
                      <b title={e.event_name}>{run || eventAr(e.event_name)}</b>
                      {d && <p dir="auto">{d}</p>}
                      {rec && <a className="gos-link" href={rec} target="_blank" rel="noopener noreferrer">▶ تسجيل هذه الصفحة</a>}
                    </div>
                  </li>
                );
              })}
            </ol>
          </>
        )}
    </section>
  );
}

export default function GrowthSessions({ user, profile, lang }) {
  const [params, setParams] = useSearchParams();
  const range = useMemo(() => readRange(params), [params]);
  const win = useMemo(() => rangeWindow(range), [range]);
  const ids = params.get('ids') ? params.get('ids').split(',') : null;
  const sid = params.get('s');
  const back = () => { const p = new URLSearchParams(params); p.delete('s'); setParams(p); };
  const setRange = (next) => {
    const p = writeRange(next);
    ['src', 'dev', 'stage', 'auth'].forEach((k) => params.get(k) && p.set(k, params.get(k)));
    setParams(p);
  };
  return (
    <GrowthShell user={user} profile={profile} lang={lang} active="sessions" title="سجل الجلسات"
                 actions={<span className="gos-top-range">{ids ? 'جلسات مختارة' : spanLabel(range.from, range.to)}</span>}>
      {sid ? <Timeline id={sid} onBack={back} /> : (
        <>
          <div className="gos-hello">
            <h2>سجل الجلسات</h2>
            <p>{ids ? 'جلسات مختارة من قائمة.' : `${rangeLabel(range)}. كل زيارة على حدة؛ اضغط أي جلسة لترى ما فعله الزائر خطوة بخطوة.`}</p>
          </div>
          {!ids && <RangeBar range={range} onChange={setRange} compare={false} />}
          <SessionList params={params} setParams={setParams} win={win} ids={ids} />
        </>
      )}
    </GrowthShell>
  );
}
